import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { GetResourceSearchResultsItem } from "@/api/getResourceSearchResults/types";
import { useFeaturedResources } from "@/modules/hub/features/ResourceLibrary/hooks/useFeaturedResources";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  getResourceTaxonomyParts,
  getResourceThumbnailUrl,
  getResourceTypeIcon,
} from "@/modules/hub/features/ResourceLibrary/hooks/useResourceCardV2";
import { getResourceTradeStyling } from "@/modules/hub/features/ResourceLibrary/utils/tradeStyling";

interface FeaturedCarouselV2Props {
  context?: "library" | "library-v2" | "podcasts" | "partner-network";
}

// Resource types that should never appear in the featured carousel.
const EXCLUDED_FEATURED_TYPES = new Set([
  "news",
  "podcast",
  "partner page",
  "partner network",
]);

export function FeaturedCarouselV2({
  context = "library",
}: FeaturedCarouselV2Props) {
  const isMobile = useIsMobile();
  const {
    featuredResources: allFeatured,
    isLoading,
    error,
  } = useFeaturedResources({ context });

  const featuredResources = allFeatured.filter(
    (resource) =>
      !EXCLUDED_FEATURED_TYPES.has((resource.resourceType || "").toLowerCase()),
  );

  const [currentIndex, setCurrentIndex] = useState(0);

  // Keep the index in range if the list size changes.
  useEffect(() => {
    setCurrentIndex((prev) =>
      Math.min(prev, Math.max(0, featuredResources.length - 1)),
    );
  }, [featuredResources.length]);

  if (isLoading) {
    return (
      <div className="mb-6 h-72 rounded-xl border border-gray-200 dark:border-slate-700 bg-gray-100 dark:bg-slate-800 animate-pulse" />
    );
  }

  if (error || featuredResources.length === 0) {
    return null;
  }

  const resource = featuredResources[currentIndex];
  const total = featuredResources.length;

  const TypeIcon = getResourceTypeIcon(resource.resourceType);
  const typeLabel = resource.resourceType || "Document";

  const taxonomyParts = getResourceTaxonomyParts(resource);
  const { tradeBadge, tradeColorClass } = getResourceTradeStyling(resource);

  // Bold the lead-in (e.g. "Inside:") when the description starts with one.
  const description = resource.searchDescription ?? "";
  const colonIndex = description.indexOf(":");
  const hasLeadIn = colonIndex > 0 && colonIndex <= 16;
  const leadIn = hasLeadIn ? description.slice(0, colonIndex + 1) : "";
  const descriptionRest = hasLeadIn
    ? description.slice(colonIndex + 1)
    : description;

  const isPartnerPage =
    resource.resourceType === "Partner Page" && resource.contentUrl;
  const href = isPartnerPage
    ? resource.contentUrl || ""
    : `/hub/resources/${resource.slug}`;

  // Renders either an internal router link or an external anchor.
  const SlideLink = ({
    className,
    children,
  }: {
    className?: string;
    children: React.ReactNode;
  }) =>
    isPartnerPage ? (
      <a
        className={className}
        href={href}
        rel="noopener noreferrer"
        target="_blank"
      >
        {children}
      </a>
    ) : (
      <Link className={className} to={href}>
        {children}
      </Link>
    );

  const goPrev = () => setCurrentIndex((i) => Math.max(0, i - 1));
  const goNext = () => setCurrentIndex((i) => Math.min(total - 1, i + 1));

  return (
    <div className="mb-6 overflow-hidden rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900">
      <div className="flex flex-col sm:flex-row sm:h-52">
        {/* Thumbnail */}
        <SlideLink className="relative block aspect-video w-full shrink-0 sm:aspect-auto sm:h-auto sm:w-[23rem]">
          {renderThumbnail(resource, TypeIcon)}
        </SlideLink>

        {/* Content */}
        <div className="flex flex-1 flex-col p-6 min-w-0 overflow-hidden">
          {/* Roles + Trade */}
          <div
            className={`flex gap-3 ${
              isMobile ? "flex-col gap-1" : "items-start justify-between"
            }`}
          >
            {/* One part per line on mobile; inline with separators on desktop */}
            <span
              className={`flex ${
                isMobile ? "flex-col" : "flex-row items-center"
              } text-xs font-semibold uppercase tracking-wide ${
                tradeColorClass ?? "text-gray-500 dark:text-gray-400"
              }`}
            >
              {taxonomyParts.map((part, i) => (
                <React.Fragment key={i}>
                  {i > 0 && !isMobile && <span className="mx-2">|</span>}
                  <span>{part}</span>
                </React.Fragment>
              ))}
            </span>
            {tradeBadge && (
              <span
                className={`shrink-0 text-xs font-semibold uppercase tracking-wide ${
                  tradeColorClass ?? "text-gray-500 dark:text-gray-400"
                }`}
              >
                {tradeBadge.label}
              </span>
            )}
          </div>

          {/* Featured · Type */}
          <div
            className={`mt-5 text-xs font-semibold uppercase tracking-wider ${
              tradeColorClass ?? "text-gray-400 dark:text-gray-500"
            }`}
          >
            Featured · {typeLabel}
          </div>

          {/* Title */}
          <SlideLink className="mt-1 block">
            <h3
              className={`text-2xl font-bold leading-snug line-clamp-2 transition-opacity hover:opacity-90 ${
                tradeColorClass ?? "text-gray-900 dark:text-white"
              }`}
            >
              {resource.title}
            </h3>
          </SlideLink>

          {/* Description */}
          {description && (
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
              {leadIn && (
                <strong className="font-semibold text-gray-800 dark:text-gray-100">
                  {leadIn}
                </strong>
              )}
              {descriptionRest}
            </p>
          )}

          {/* Footer: type · carousel controls · view */}
          <div className="mt-auto flex items-center justify-between pt-4 text-xs text-gray-500 dark:text-gray-400">
            <span className="flex items-center gap-1.5">
              <TypeIcon className="h-3.5 w-3.5" />
              {typeLabel}
            </span>

            {total > 1 && (
              <div className="flex items-center gap-3">
                <button
                  aria-label="Previous featured resource"
                  className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  disabled={currentIndex === 0}
                  onClick={goPrev}
                  type="button"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>

                <div className="flex items-center gap-1.5">
                  {featuredResources.map((item, index) => (
                    <button
                      aria-label={`Go to featured resource ${index + 1}`}
                      className={`h-1.5 w-1.5 rounded-full transition-colors ${
                        index === currentIndex
                          ? "bg-red-700 dark:bg-red-500"
                          : "bg-gray-300 dark:bg-gray-600"
                      }`}
                      key={item.id}
                      onClick={() => setCurrentIndex(index)}
                      type="button"
                    />
                  ))}
                </div>

                <button
                  aria-label="Next featured resource"
                  className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  disabled={currentIndex === total - 1}
                  onClick={goNext}
                  type="button"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}

            <SlideLink
              className={`font-bold ${
                tradeColorClass ?? "text-gray-900 dark:text-white"
              }`}
            >
              View
            </SlideLink>
          </div>
        </div>
      </div>
    </div>
  );
}

function renderThumbnail(
  resource: GetResourceSearchResultsItem,
  TypeIcon: ReturnType<typeof getResourceTypeIcon>,
) {
  const thumbnailUrl = getResourceThumbnailUrl(resource);

  if (thumbnailUrl) {
    return (
      <img
        alt={resource.title}
        className="absolute inset-0 h-full w-full object-cover"
        src={thumbnailUrl}
      />
    );
  }

  return (
    <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-slate-800">
      <TypeIcon className="h-10 w-10 text-gray-400 dark:text-gray-500" />
    </div>
  );
}
